import { Rgb, Scene, WallpaperStyleInfo, darken, lighten, luminance, prng, rgba, share } from '../wallpaper-core';

export const INFO: WallpaperStyleInfo = {
  key: 'terrazzo', label: 'Terrazzo',
  blurb: 'Colorful stone chips scattered in a smooth base, the terrazzo floor look',
  params: [ share( 'chips', 'Chips', 50 ), share( 'size', 'Size', 40 ) ],
};

interface Pt { x: number; y: number; }

/** a chip on the floor: its centre, its convex outline (relative to the centre) and the circle the outline fits in */
interface Chip { x: number; y: number; r: number; pts: Pt[]; }

/**
 * Terrazzo: flat stone chips of every tile set in a matte cement of the first tile. The chips come
 * from a jittered grid, each cell drawing its own from a (seed, row, column) generator, and a
 * spatial hash keeps them from overlapping, as in the real floor: big chips first, small ones
 * after, tiny bits sized to whatever gap is left.
 */
export function draw( { ctx, w, h, box, colors, rand, p, seed }: Scene ): void {
  const base = colors[ 0 ];
  // the cement: the first tile flat, with a few huge soft blots a shade off it so it is not a
  // fill. A near black cement has nowhere darker to go, so there the blots are lighter
  const dark = luminance( base ) < 0.5;
  const shade = dark ? lighten( base, 0.05 ) : darken( base, 0.04 );
  ctx.fillStyle = rgba( base, 1 );
  ctx.fillRect( 0, 0, w, h );
  const blots = 3 + Math.floor( rand() * 2 );
  for ( let b = 0; b < blots; b++ ) {
    const x = box.x + rand() * box.w;
    const y = box.y + rand() * box.h;
    const r = box.w * ( 0.4 + rand() * 0.4 );
    const g = ctx.createRadialGradient( x, y, 0, x, y, r );
    g.addColorStop( 0, rgba( shade, 1 ) );
    g.addColorStop( 1, rgba( shade, 0 ) );
    ctx.fillStyle = g;
    ctx.fillRect( 0, 0, w, h );
  }

  // the chips walk tiles 2..n in order, so every tile shows in equal measure; with one tile to
  // walk it is that tile at three lightness steps; 15% of the chips are the grey stone bits, the
  // cement itself a step off (lighter on a dark cement, or they would vanish)
  const last = colors[ colors.length - 1 ];
  const walk = colors.length > 2 ? colors.slice( 1 ) : [ lighten( last, 0.3 ), last, darken( last, 0.3 ) ];
  const stone = dark ? lighten( base, 0.16 ) : darken( base, 0.15 );
  let walked = 0;
  const pick = ( g: () => number ): Rgb => {
    const grey = g() < 0.15;
    const jitter = ( g() - 0.5 ) * 0.16;
    if ( grey ) return stone;
    const c = walk[ walked++ % walk.length ];
    return jitter >= 0 ? lighten( c, jitter ) : darken( c, -jitter );
  };

  // Chips sets the grid: a cell of the box's height over 8 (a few big chips) to 28 (a packed
  // field); Size scales every chip against its cell, from speckles to slabs that spill over it
  const cell = box.h / ( 8 + p[ 0 ] * 20 );
  const size = 0.45 + p[ 1 ] * 1.6;
  // the cement line two chips keep between them
  const gap = cell * size * 0.05;
  const c0 = Math.floor( ( 0 - box.x ) / cell ) - 2;
  const c1 = Math.ceil( ( w - box.x ) / cell ) + 2;
  const r0 = Math.floor( ( 0 - box.y ) / cell ) - 2;
  const r1 = Math.ceil( ( h - box.y ) / cell ) + 2;

  // the spatial hash of what is placed, one bucket per cell, read two cells around a point
  // (the widest chip spills less than that)
  const buckets = new Map<number, Chip[]>();
  const keyOf = ( x: number, y: number ) => Math.floor( ( y - box.y ) / cell ) * 65536 + Math.floor( ( x - box.x ) / cell );
  const reach = 2;
  const near = ( x: number, y: number, test: ( q: Chip ) => boolean ): boolean => {
    const bc = Math.floor( ( x - box.x ) / cell ), br = Math.floor( ( y - box.y ) / cell );
    for ( let r = br - reach; r <= br + reach; r++ ) {
      for ( let c = bc - reach; c <= bc + reach; c++ ) {
        const list = buckets.get( r * 65536 + c );
        if ( !list ) continue;
        for ( const q of list ) if ( test( q ) ) return true;
      }
    }
    return false;
  };
  const put = ( q: Chip ) => {
    const k = keyOf( q.x, q.y );
    const list = buckets.get( k );
    if ( list ) list.push( q ); else buckets.set( k, [ q ] );
  };

  // one rounded polygon, filled flat: every corner is cut back along its two sides and bridged by
  // a quadratic through the vertex. arcTo would be the obvious call, but on a nearly straight
  // corner it throws its tangent point hundreds of pixels away and paints a hairline across the
  // whole wallpaper, so the corners are cut by hand
  const fill = ( q: Chip, col: Rgb ) => {
    if ( q.x + q.r < 0 || q.x - q.r > w || q.y + q.r < 0 || q.y - q.r > h ) return;
    const { x, y, pts } = q;
    const m = pts.length;
    if ( m < 3 ) return;
    const round = q.r * 0.16;
    ctx.fillStyle = rgba( col, 1 );
    ctx.beginPath();
    for ( let i = 0; i < m; i++ ) {
      const a = pts[ ( i + m - 1 ) % m ], v = pts[ i ], b = pts[ ( i + 1 ) % m ];
      const ax = a.x - v.x, ay = a.y - v.y, bx = b.x - v.x, by = b.y - v.y;
      const la = Math.hypot( ax, ay ) || 1, lb = Math.hypot( bx, by ) || 1;
      // never past the middle of a side, so two corners of a short side never cross
      const t = Math.min( round, la * 0.4, lb * 0.4 );
      const ix = x + v.x + ax / la * t, iy = y + v.y + ay / la * t;
      const ox = x + v.x + bx / lb * t, oy = y + v.y + by / lb * t;
      if ( i === 0 ) ctx.moveTo( ix, iy ); else ctx.lineTo( ix, iy );
      ctx.quadraticCurveTo( x + v.x, y + v.y, ox, oy );
    }
    ctx.closePath();
    ctx.fill();
  };

  // a chip that fits: its outline is cut first, then held against every neighbour it could touch
  // (a separating axis test with the cement line as margin), so no two chips ever overlap
  const settle = ( x: number, y: number, r: number, g: () => number ): boolean => {
    const q = shard( x, y, r, g );
    if ( near( x, y, o => Math.hypot( o.x - x, o.y - y ) < o.r + q.r + gap && !apart( o, q, gap ) ) ) return false;
    put( q );
    fill( q, pick( g ) );
    return true;
  };

  // Every cell draws what it wants from its own generator: two or three chips, most of them
  // small, some big (the bimodal sizes are what says terrazzo rather than confetti), each jittered
  // up to 60% of a cell over its borders so the grid never shows and the field continues past
  // the box. The cells keep their generators for the tiny bits after
  interface Wish { x: number; y: number; r: number; g: () => number; }
  const big: Wish[] = [];
  const small: Wish[] = [];
  const cells: { r: number; c: number; g: () => number }[] = [];
  for ( let r = r0; r < r1; r++ ) {
    for ( let c = c0; c < c1; c++ ) {
      const g = prng( ( seed * 31 + r * 1000003 + c * 7919 ) >>> 0 );
      const count = 2 + ( g() < p[ 0 ] ? 1 : 0 );
      for ( let i = 0; i < count; i++ ) {
        const x = box.x + ( c + 0.5 + ( g() - 0.5 ) * 1.2 ) * cell;
        const y = box.y + ( r + 0.5 + ( g() - 0.5 ) * 1.2 ) * cell;
        const large = g() < 0.3;
        const r2 = cell * size * ( large ? 0.35 + g() * 0.15 : 0.18 + g() * 0.12 );
        ( large ? big : small ).push( { x, y, r: r2, g } );
      }
      cells.push( { r, c, g } );
    }
  }
  // big chips take their places first, then the small ones fit around them
  for ( const q of big ) settle( q.x, q.y, q.r, q.g );
  for ( const q of small ) settle( q.x, q.y, q.r, q.g );

  // the tiny bits: a few tries per cell, each one sized to the gap it lands in (never bigger than
  // a small chip), so the cement between the chips is never bald, as on the real floor
  const tries = 4 + Math.round( p[ 0 ] * 5 );
  const bitMin = cell * size * 0.035;
  for ( const { r, c, g } of cells ) {
    for ( let i = 0; i < tries; i++ ) {
      const x = box.x + ( c + 0.5 + ( g() - 0.5 ) * 1.1 ) * cell;
      const y = box.y + ( r + 0.5 + ( g() - 0.5 ) * 1.1 ) * cell;
      let clear = cell * size * ( 0.05 + g() * 0.13 );
      // every neighbour is measured, never the first one that is close enough, or a bit would
      // still grow into the chip behind it
      near( x, y, o => {
        if ( Math.hypot( o.x - x, o.y - y ) - o.r - gap < clear ) clear = Math.min( clear, distance( o, x, y ) - gap );
        return false;
      } );
      if ( clear < bitMin ) continue;
      settle( x, y, clear, g );
    }
  }
}

/** a chip's outline: 5 to 8 corners at uneven angles and radii, squashed into a shard and turned, then its convex hull */
function shard( x: number, y: number, r: number, g: () => number ): Chip {
  const n = 5 + Math.floor( g() * 4 );
  const rot = g() * Math.PI * 2;
  const squash = 0.62 + g() * 0.38;
  const cr = Math.cos( rot ), sr = Math.sin( rot );
  const raw: Pt[] = [];
  let bound = 0;
  for ( let i = 0; i < n; i++ ) {
    const a = ( i + ( g() - 0.5 ) * 0.8 ) / n * Math.PI * 2;
    const rr = r * ( 0.6 + g() * 0.4 );
    const px = Math.cos( a ) * rr, py = Math.sin( a ) * rr * squash;
    const q = { x: px * cr - py * sr, y: px * sr + py * cr };
    bound = Math.max( bound, Math.hypot( q.x, q.y ) );
    raw.push( q );
  }
  return { x, y, r: bound, pts: hull( raw ) };
}

/** the convex hull of a handful of points (monotone chain), every turn to the same side */
function hull( pts: Pt[] ): Pt[] {
  const s = pts.slice().sort( ( a, b ) => a.x - b.x || a.y - b.y );
  const cross = ( o: Pt, a: Pt, b: Pt ) => ( a.x - o.x ) * ( b.y - o.y ) - ( a.y - o.y ) * ( b.x - o.x );
  const lower: Pt[] = [];
  for ( const q of s ) {
    while ( lower.length >= 2 && cross( lower[ lower.length - 2 ], lower[ lower.length - 1 ], q ) <= 0 ) lower.pop();
    lower.push( q );
  }
  const upper: Pt[] = [];
  for ( let i = s.length - 1; i >= 0; i-- ) {
    const q = s[ i ];
    while ( upper.length >= 2 && cross( upper[ upper.length - 2 ], upper[ upper.length - 1 ], q ) <= 0 ) upper.pop();
    upper.push( q );
  }
  lower.pop();
  upper.pop();
  return lower.concat( upper );
}

/** true when two chips keep at least `gap` between them along one of their sides' normals (a safe no overlap test for convex outlines) */
function apart( a: Chip, b: Chip, gap: number ): boolean {
  const dx = b.x - a.x, dy = b.y - a.y;
  return separated( a.pts, b.pts, dx, dy, gap ) || separated( b.pts, a.pts, -dx, -dy, gap );
}

function separated( a: Pt[], b: Pt[], dx: number, dy: number, gap: number ): boolean {
  const n = a.length;
  for ( let i = 0; i < n; i++ ) {
    const p = a[ i ], q = a[ ( i + 1 ) % n ];
    const len = Math.hypot( q.y - p.y, q.x - p.x );
    if ( !len ) continue;
    const nx = ( q.y - p.y ) / len, ny = ( p.x - q.x ) / len;
    let minA = Infinity, maxA = -Infinity, minB = Infinity, maxB = -Infinity;
    for ( const v of a ) { const s = v.x * nx + v.y * ny; if ( s < minA ) minA = s; if ( s > maxA ) maxA = s; }
    for ( const v of b ) { const s = ( v.x + dx ) * nx + ( v.y + dy ) * ny; if ( s < minB ) minB = s; if ( s > maxB ) maxB = s; }
    if ( minB - maxA >= gap || minA - maxB >= gap ) return true;
  }
  return false;
}

/** how far a point is from a chip's outline (0 inside it) */
function distance( q: Chip, x: number, y: number ): number {
  const px = x - q.x, py = y - q.y;
  const n = q.pts.length;
  let best = Infinity, inside = true;
  for ( let i = 0; i < n; i++ ) {
    const a = q.pts[ i ], b = q.pts[ ( i + 1 ) % n ];
    const ex = b.x - a.x, ey = b.y - a.y;
    const t = Math.max( 0, Math.min( 1, ( ( px - a.x ) * ex + ( py - a.y ) * ey ) / ( ex * ex + ey * ey ) ) );
    const d = Math.hypot( px - a.x - ex * t, py - a.y - ey * t );
    if ( d < best ) best = d;
    if ( ex * ( py - a.y ) - ey * ( px - a.x ) < 0 ) inside = false;
  }
  return inside ? 0 : best;
}
